import {
	dateFormats,
	DateObject,
	EventIdentifier,
	type LabelTypeColors,
	StakingType,
	type StatsBarProps,
	useAppTheme,
	useIntl,
	useSettings,
	useVoteDistributionColors,
	type VoteDistributionProps
} from '@capsicum/common';
import { useFanTokenStakingAnalytics } from '@capsicum/socios-core';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useMemo, useState } from 'react';

import { getSurveyAnswerAtom, selectedAnswerAtom } from '../data-access';
import { type AnimationStatus } from '../feature';

import { useSurveyById } from './useSurveyById';
import { useSurveyStatusColor } from './useSurveyStatusColor';

export type AnimationOverlayStatus = (typeof AnimationStatus)[keyof typeof AnimationStatus];

const PARTNER_BANNER = 'partner_banner';
const PARTNER_LOGO = 'partner_logo';

export const useSurveyDetails = ({ surveyId }: { surveyId: string }) => {
	const { isSurveyLoading, refetchSurvey, surveyData, surveyError } = useSurveyById(surveyId);
	const { voteDistributionColors } = useVoteDistributionColors();

	const [refreshing, setRefreshing] = useState<boolean>(false);
	const [selectedAnswer, setSelectedAnswer] = useAtom(selectedAnswerAtom);
	const surveyAnswerAtom = useMemo(() => getSurveyAnswerAtom(surveyId), [surveyId]);
	const surveyV3Answer = useAtomValue(surveyAnswerAtom);

	const { theme } = useAppTheme();
	const { $t, getLocalizedField } = useIntl();

	const onAnswerChange = useCallback((answerId: string) => {
		setSelectedAnswer(answerId);
	}, []);

	const onRefresh = useCallback(async () => {
		setRefreshing(true);
		await refetchSurvey();
		setRefreshing(false);
	}, [refetchSurvey]);

	const {
		settings: { isPoblanoEnabled, isSurveyV3Enabled, showTokenNeededLink }
	} = useSettings();

	const getSurveyStatusColor = useSurveyStatusColor();

	const { sendFanTokenStakingEvent } = useFanTokenStakingAnalytics({
		currencyId: surveyData?.currencyId,
		currentPage: 'poll_page',
		eventIdentifier: EventIdentifier.TriggerClickFtStaking,
		partnerId: surveyData?.partnerId,
		transactionType: StakingType.STAKE
	});

	const isLoading = isSurveyLoading;
	const isError = surveyError || !surveyData;

	if (isLoading || isError) {
		return {
			isError,
			// GET request(s)
			isLoading,
			onAnswerChange,
			// CALLBACKS
			onRefresh,
			// STATE
			refreshing,
			selectedAnswer
		} as const;
	}

	const partnerBg = surveyData.images?.find(image => image.imageType?.type === PARTNER_BANNER)?.url || '';
	const partnerLogo = surveyData.images?.find(image => image.imageType?.type === PARTNER_LOGO)?.url || '';
	const partnerName = surveyData.partnerName || '';

	const currencyId = surveyData.currencyId;
	const currencyShortName = surveyData.currencyShortName;
	const custodialHasEnoughBalance = surveyData.hasEnoughBalance;

	const hasUserVoted = Boolean(surveyData.hasUserVoted);
	const isBound = surveyData.isBound;
	const tokenLimit = surveyData.tokenLimit;
	const isCapped = Boolean(tokenLimit);
	const openAt = dateFormats.format(surveyData.openAt, 'DD/MM/YYYY');
	const closeAt = dateFormats.format(surveyData.closeAt, 'DD/MM/YYYY');
	const votedAt = dateFormats.format(surveyData.votedAt, 'DD/MM/YYYY');
	const surveyType = surveyData.type;
	const disclosedAt = surveyData.disclosedAt || '';

	const information = getLocalizedField('information', surveyData);
	const informationLink = surveyData.informationUrl;
	const introductionText = getLocalizedField('introductionText', surveyData);
	const surveyQuestion = getLocalizedField('question', surveyData);

	const surveyVersion = surveyData.version;
	const isNCWFlow = isPoblanoEnabled && isSurveyV3Enabled && surveyVersion === 3;
	const isNCWUserVotePendingBlockchain = !hasUserVoted && Boolean(surveyV3Answer);
	// TODO: Check with BE what to do if survey has version === 3 but one of the config flags is off
	// Do we proceed with Custodial flow or do we have to return an error?

	const answers = surveyData.answers || [];

	const intlAnswers = answers.map(answer => {
		const intlAnswer = getLocalizedField('answer', answer);

		return {
			...answer,
			answer: intlAnswer
		};
	});

	const intlAvailableUntilCloseAt = $t('survey.details.availableUntilCloseAt', { closeAt });
	const intlAvailableOnStartAt = $t('survey.details.availableOnStartAt', { openAt });
	const intlExpiredOnCloseAt = $t('survey.details.expiredOnCloseAt', { closeAt });
	const intlUserVotedOnVotedAt = $t('survey.details.userVotedOnVotedAt', { votedAt });
	const intlClubWillAbideResult = $t('survey.details.clubWillAbideResult');
	const intlAboutThisPoll = $t('survey.details.aboutThisPoll');
	const intlVoteDistribution = $t('survey.details.voteDistribution');
	const intlCappedAtTokenLimit = $t('survey.details.cappedAtTokenLimit', { tokenLimit });
	const intlBinding = $t('survey.details.binding');
	const intlSubmitVote = $t('survey.details.submitVote');
	const intlTokenAmountNeeded = $t('survey.details.tokenAmountNeeded', { currencyShortName });
	const intlTokenBuyLink = $t('survey.details.tokenBuyLink', { currencyShortName });

	const isSurveyClosed = ['closed', 'closed-blockchain-pending', 'closed-release-wallet-limit'].includes(
		surveyData.status || 'N/A'
	);
	const isSurveyScheduled = ['scheduled'].includes(surveyData.status || 'N/A');
	const isSurveyVotable = Boolean(!isSurveyClosed && !isSurveyScheduled && !hasUserVoted);

	const getDateLabelText = () => {
		if (hasUserVoted) {
			return intlUserVotedOnVotedAt;
		}

		if (isSurveyClosed) {
			return intlExpiredOnCloseAt;
		}

		if (isSurveyScheduled) {
			return intlAvailableOnStartAt;
		}

		return intlAvailableUntilCloseAt;
	};

	const dateLabelProps = {
		label: getDateLabelText(),
		variant: hasUserVoted ? 'grey' : getSurveyStatusColor(surveyData.status) || 'blue'
	} satisfies {
		label: string;
		variant: LabelTypeColors;
	};

	const votesCollectedData: StatsBarProps = {
		leftAdditionalText: `${Number(surveyData.currentQuorumPercentage || '0')}%`,
		leftText: $t('survey.votesCollected'),
		progressBar: {
			barColor: theme.colors.backgrounds.brand.enabled,
			labelColor: theme.colors.backgrounds.brand.enabled,
			labelType: 'none',
			progress: Number(surveyData.currentQuorumPercentage || '0')
		},
		rightAdditionalText: $t('survey.votesRequired'),
		rightPercentage: parseInt(surveyData.quorumPercentage || '0'),
		testID: 'surveyDetails-statistics-votesCollected',
		textColors: {
			leftAdditionalText: theme.colors.text.brand.enabled
		}
	};

	const voteDistributionData: VoteDistributionProps = {
		testID: 'surveyDetails-statistics-voteDistribution',
		votes: intlAnswers.map((answer, index) => {
			return {
				leftText: answer.answer || '',
				progressBar: {
					barColor:
						voteDistributionColors[index % voteDistributionColors.length] ||
						theme.colors.backgrounds.status.first,
					labelColor: theme.colors.text.main.enabled,
					labelType: 'none',
					progress: parseFloat(answer.votePercentage || '0')
				},
				rightPercentage: parseFloat(answer.votePercentage || '0')
			};
		})
	};

	const showVotesCollected = votesCollectedData && (isSurveyClosed || hasUserVoted);

	const showVoteDistribution = () => {
		if (!voteDistributionData?.votes?.length) {
			return false;
		}

		if (surveyType === 'integrity' && isBound) {
			const currentDate = new DateObject().getTime();
			const disclosedDate = new DateObject(disclosedAt).getTime();

			return disclosedDate ? currentDate > disclosedDate : isSurveyClosed;
		}

		return hasUserVoted || isSurveyClosed;
	};

	return {
		currencyId,
		currencyShortName,
		custodialHasEnoughBalance,
		dateLabelProps,
		hasUserVoted,
		information,
		informationLink,
		intlAboutThisPoll,
		intlAnswers,
		intlBinding,
		intlCappedAtTokenLimit,
		// TRANSLATIONS
		intlClubWillAbideResult,
		intlSubmitVote,
		intlTokenAmountNeeded,
		intlTokenBuyLink,
		intlVoteDistribution,
		introductionText,
		isBound,
		isCapped,
		isError,
		// GET request(s)
		isLoading,
		isNCWFlow,
		isNCWUserVotePendingBlockchain,
		isSurveyClosed,
		isSurveyVotable,
		onAnswerChange,
		// CALLBACKS
		onRefresh,
		partnerBg,
		partnerLogo,
		partnerName,
		// STATE
		refreshing,
		selectedAnswer,
		sendFanTokenStakingEvent,
		showTokenNeededLink,
		showVoteDistribution: showVoteDistribution(),
		showVotesCollected,
		surveyQuestion,
		voteDistributionData,
		votesCollectedData
	};
};
